import { useQuery, useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { getSdk } from '@/lib/sdk';
import type { Ulid } from '@/types';

export const userKeys = {
  all: ['users'] as const,
  profile: (username: string) => ['users', 'profile', username] as const,
  profileById: (ulid: Ulid) => ['users', 'profileById', ulid] as const,
  suggestions: (limit?: number) => ['users', 'suggestions', limit] as const,
  followers: (username: string) => ['users', 'followers', username] as const,
  following: (username: string) => ['users', 'following', username] as const,
  badges: (userId: Ulid) => ['users', 'badges', userId] as const,
  blocked: () => ['users', 'blocked'] as const,
  muted: () => ['users', 'muted'] as const,
};

/**
 * Fetch a user profile by username.
 */
export function useUserProfile(username: string | undefined) {
  const sdk = getSdk();

  return useQuery({
    queryKey: userKeys.profile(username ?? ''),
    queryFn: async () => {
      const response = await sdk.getUserProfile(username!);
      return response.data;
    },
    enabled: Boolean(username),
  });
}

/**
 * Fetch a user profile by ULID.
 */
export function useUserProfileById(ulid: Ulid | undefined) {
  const sdk = getSdk();

  return useQuery({
    queryKey: userKeys.profileById(ulid ?? ''),
    queryFn: async () => {
      const response = await sdk.getUserProfileById(ulid!);
      return response.data;
    },
    enabled: Boolean(ulid),
  });
}

/**
 * Fetch suggested users ("Who to Follow").
 */
export function useUserSuggestions(limit?: number) {
  const sdk = getSdk();

  return useQuery({
    queryKey: userKeys.suggestions(limit),
    queryFn: () => sdk.getUserSuggestions(limit),
  });
}

/**
 * Fetch a user's followers.
 */
export function useFollowers(username: string | undefined) {
  const sdk = getSdk();

  return useQuery({
    queryKey: userKeys.followers(username ?? ''),
    queryFn: () => sdk.getProfileFollowers(username!),
    enabled: Boolean(username),
  });
}

/**
 * Fetch a user's following list.
 */
export function useFollowing(username: string | undefined) {
  const sdk = getSdk();

  return useQuery({
    queryKey: userKeys.following(username ?? ''),
    queryFn: () => sdk.getProfileFollowing(username!),
    enabled: Boolean(username),
  });
}

/**
 * Fetch user badges.
 */
export function useUserBadges(userId: Ulid | undefined) {
  const sdk = getSdk();

  return useQuery({
    queryKey: userKeys.badges(userId ?? ''),
    queryFn: async () => {
      const response = await sdk.getUserBadges(userId!);
      return response.data;
    },
    enabled: Boolean(userId),
  });
}

type FollowMutationVars = { username: string; isFollowing: boolean };
type FollowMutationContext = { previousProfile: unknown };

/**
 * Follow/unfollow a user by username.
 */
export function useFollowUser() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, FollowMutationVars, FollowMutationContext>({
    mutationFn: async ({ username, isFollowing }: FollowMutationVars) => {
      if (isFollowing) {
        await sdk.unfollowUser(username);
      } else {
        await sdk.followUser(username);
      }
      return !isFollowing;
    },
    onMutate: async ({ username, isFollowing }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: userKeys.profile(username) });

      // Snapshot the previous value
      const previousProfile = queryClient.getQueryData(userKeys.profile(username));

      // Optimistically update the cache
      queryClient.setQueryData(userKeys.profile(username), (old: Record<string, unknown> | undefined) => {
        if (!old) return old;
        const engagement = old.ProfileEngagement || old.profileEngagement || {};
        return {
          ...old,
          ProfileEngagement: { ...engagement, isFollowing: !isFollowing },
          profileEngagement: { ...engagement, isFollowing: !isFollowing },
        };
      });

      return { previousProfile };
    },
    onError: (_err, { username }, context) => {
      // Roll back to the previous value on error
      if (context?.previousProfile) {
        queryClient.setQueryData(userKeys.profile(username), context.previousProfile);
      }
    },
    onSettled: async (_data, _error, { username }) => {
      // Clear SDK cache and refresh to ensure persistence across reloads
      await sdk.clearCache();
      queryClient.invalidateQueries({ queryKey: userKeys.profile(username) });
      queryClient.invalidateQueries({ queryKey: userKeys.suggestions() });
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/**
 * Update the current user's profile.
 */
export function useUpdateProfile() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Partial<{
      displayName: string;
      bio: string;
      avatarUrl: string;
      background: string;
      website: string;
    }>) => {
      return sdk.updateCurrentUser(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/**
 * Paginated bookmarked posts for the current user.
 */
export function useBookmarksFeed(enabled = true) {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: ['bookmarks'] as const,
    queryFn: async ({ pageParam }) => {
      return sdk.getBookmarks(pageParam ?? null);
    },
    getNextPageParam: (lastPage) => (lastPage as Record<string, unknown>).nextCursor as string | null | undefined ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
  });
}

/**
 * Paginated liked posts for a user by username.
 */
export function useLikedFeed(username?: string) {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: ['liked', username] as const,
    queryFn: async ({ pageParam }) => {
      return sdk.getProfileLikesFeed(username!, pageParam ?? null);
    },
    getNextPageParam: (lastPage) => (lastPage as Record<string, unknown>).nextCursor as string | null | undefined ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(username),
  });
}

/**
 * Block/unblock a user.
 */
export function useBlockUser() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, isBlocked }: { userId: Ulid; isBlocked: boolean }) => {
      if (isBlocked) {
        await sdk.unblockUser(userId);
      } else {
        await sdk.blockUser(userId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/**
 * Mute/unmute a user.
 */
export function useMuteUser() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, isMuted }: { userId: Ulid; isMuted: boolean }) => {
      if (isMuted) {
        await sdk.unmuteUser(userId);
      } else {
        await sdk.muteUser(userId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}
